import React from 'react';
import { Box, Typography, List, ListItem, ListItemText } from '@mui/material';
import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import ArchiveIcon from '@mui/icons-material/ArchiveOutlined';
import AssignmentIcon from '@mui/icons-material/AssignmentOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import { colors } from '../colors';
import { useNavigate } from 'react-router-dom';

const adminSections = [
    {
        name: 'Overview',
        icon: <DashboardIcon />,
        path: 'Overview',
        children: [
            { name: 'Wave Overview', path: 'WaveOverview' },
            { name: 'Instance Overview', path: 'InstanceOverview' },
        ],
    },
    {
        name: 'Dashboard',
        icon: <DashboardIcon />,
        path: 'DashboardView',
        children: [
            { name: 'View Survey', path: 'ViewSurvey' },
            { name: 'Create Survey', path: 'CreateSurvey' },
        ],
    },
    {
        name: 'Templates',
        icon: <AssignmentIcon />,
        path: 'TemplateManagement',
        children: [
            { name: 'Add Template', path: 'TemplateManagement' },
            { name: 'Create Template', path: '/admin/templates/create' },
        ],
    },
    { name: 'Archived', icon: <ArchiveIcon />, path: 'ArchivedPage' },
    { name: 'Settings', icon: <SettingsIcon />, path: 'Settings' },
];

const AdminNavigationPane = ({ currentPath, onNavigate }) => {
    const navigate = useNavigate();

    const handleNavigation = (path) => {
        if (path.startsWith('/')) {
            // For absolute paths, navigate directly without modifying the URL
            navigate(path);
        } else {
            // For relative paths, update the view within the dashboard
            navigate('/admin/dashboard', { state: { view: path } });
            onNavigate(path);
        }
    };


    return (
        <Box
            sx={{
                width: '17%',
                flexShrink: 0,
                color: colors.primaryApplicationText,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                padding: '15px 0px 0px 15px',
            }}
        >
            {/* Admin Panel Title */}
            <Box sx={{ mb: 2, paddingLeft: '15px' }}>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 'bold',
                        fontSize: '1.2rem',
                        letterSpacing: '1px',
                        color: colors.racineRedDark,
                    }}
                >
                    Admin Panel
                </Typography>
            </Box>

            {/* Separator */}
            <Box
                sx={{
                    height: '1px',
                    width: '100%',
                    backgroundColor: colors.racineRedDark,
                    mb: 2,
                }}
            />

            {/* Navigation List */}
            <List>
                {adminSections.map((section, index) => (
                    <React.Fragment key={index}>
                        {/* Main Section */}
                        <ListItem
                            button
                            onClick={() => handleNavigation(section.path)}
                            sx={{
                                backgroundColor: section.path === currentPath ? '#e3f2fd' : 'transparent',
                                color: colors.primaryApplicationText,
                                '&:hover': {
                                    backgroundColor: '#e3f2fd',
                                },
                                borderRadius: '8px',
                                marginBottom: '5px',
                                padding: '10px 15px',
                                transition: 'background-color 0.3s ease, color 0.3s ease',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Box sx={{ fontSize: '1rem' }}>{section.icon}</Box>
                                <ListItemText
                                    primary={section.name}
                                    primaryTypographyProps={{
                                        fontSize: '1rem',
                                        fontWeight: 'bold',
                                    }}
                                />
                            </Box>
                        </ListItem>

                        {/* Sub-options */}
                        {section.children &&
                            section.children.map((child, childIndex) => (
                                <ListItem
                                    key={`${index}-${childIndex}`}
                                    button
                                    onClick={() => handleNavigation(child.path)}
                                    sx={{
                                        backgroundColor: child.path === currentPath ? '#e8f5e9' : 'transparent',
                                        color: colors.primaryApplicationText,
                                        '&:hover': {
                                            backgroundColor: '#e8f5e9',
                                        },
                                        padding: '8px 30px',
                                        borderRadius: '8px',
                                        margin: '3px 0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    <Box sx={{ fontSize: '1rem', color: '#1976d2' }}>•</Box> {/* Bullet for hierarchy */}
                                    <ListItemText
                                        primary={child.name}
                                        primaryTypographyProps={{
                                            fontSize: '0.8rem',
                                            fontWeight: 'bold',
                                        }}
                                    />
                                </ListItem>
                            ))}
                    </React.Fragment>
                ))}
            </List>
        </Box>
    );
};

export default AdminNavigationPane;
